import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import MobileHeader from "../../components/MobileHeader";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { Badge, Button, Card, Input, Label, StatusBadge } from "../../components/ui-kit";
import { CheckCircle, ForkKnife, Package, PencilSimple, Storefront, XCircle } from "@phosphor-icons/react";

const emptyProduct = {
  name: "",
  description: "",
  price: "",
  image_url: "",
  image_file: null,
  category: "",
  available: true,
};

function cleanOptional(value) {
  const text = String(value || "").trim();
  return text ? text : null;
}

function paymentTone(status) {
  if (status === "CONFIRMED") return "success";
  if (status === "REJECTED" || status === "FAILED") return "danger";
  if (status === "PAYMENT_PROOF_SENT") return "primary";
  return "warning";
}

export default function MerchantDashboard() {
  const [merchants, setMerchants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [busy, setBusy] = useState("");
  const [productForm, setProductForm] = useState(emptyProduct);
  const [editingProductId, setEditingProductId] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [note, setNote] = useState("");

  const selectedMerchant = useMemo(
    () => merchants.find((merchant) => merchant.id === selectedMerchantId) || merchants[0] || null,
    [merchants, selectedMerchantId],
  );

  const merchantOrders = useMemo(() => {
    if (!selectedMerchant) return orders;
    return orders.filter((order) => order.merchant_id === selectedMerchant.id);
  }, [orders, selectedMerchant]);

  const stats = useMemo(() => {
    const activeOrders = merchantOrders.filter((order) => !["DELIVERED", "CANCELLED"].includes(order.status)).length;
    const sales = merchantOrders
      .filter((order) => order.payment_status === "CONFIRMED" || order.status === "DELIVERED")
      .reduce((sum, order) => sum + Number(order.total_amount || order.items_subtotal || 0), 0);
    const net = merchantOrders
      .filter((order) => order.payment_status === "CONFIRMED" || order.status === "DELIVERED")
      .reduce((sum, order) => sum + Number(order.merchant_net || 0), 0);
    return { activeOrders, sales, net };
  }, [merchantOrders]);

  const productCategories = useMemo(() => {
    const categories = products.map((product) => product.category || "General").filter(Boolean);
    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filteredProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    return products.filter((product) =>
      (productCategoryFilter === "all" || (product.category || "General") === productCategoryFilter) &&
      (!q || [product.name, product.description, product.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)))
    );
  }, [productCategoryFilter, productQuery, products]);

  const load = async () => {
    try {
      const { data } = await api.get("/merchant/me");
      const merchantList = Array.isArray(data?.merchants) ? data.merchants : [];
      setMerchants(merchantList);
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
      setSelectedMerchantId((current) => current || merchantList[0]?.id || "");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadProducts = async (merchantId) => {
    if (!merchantId) {
      setProducts([]);
      return;
    }
    try {
      const { data } = await api.get("/merchant/products", { params: { merchant_id: merchantId } });
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadProducts(selectedMerchant?.id);
  }, [selectedMerchant?.id]);

  const orderAction = async (orderId, endpoint, success) => {
    setBusy(`${orderId}:${endpoint}`);
    try {
      await api.post(`/merchant/orders/${orderId}/${endpoint}`, { note: note.trim() || null });
      toast.success(success);
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const submitProduct = async () => {
    if (!selectedMerchant) {
      toast.error("Selectionnez un partenaire");
      return;
    }
    const payload = {
      name: productForm.name.trim(),
      description: cleanOptional(productForm.description),
      price: Number(productForm.price),
      image_url: cleanOptional(productForm.image_url),
      category: cleanOptional(productForm.category),
      available: Boolean(productForm.available),
    };
    setBusy("product");
    try {
      let product;
      if (editingProductId) {
        const { data } = await api.put(`/merchant/products/${editingProductId}`, payload);
        product = data;
        toast.success("Produit modifie");
      } else {
        const { data } = await api.post("/merchant/products", payload, { params: { merchant_id: selectedMerchant.id } });
        product = data;
        toast.success("Produit ajoute");
      }
      if (productForm.image_file && product?.id) {
        const form = new FormData();
        form.append("file", productForm.image_file);
        await api.post(`/merchant/products/${product.id}/image`, form, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Image produit envoyee");
      }
      setProductForm(emptyProduct);
      setEditingProductId("");
      await loadProducts(selectedMerchant.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const editProduct = (product) => {
    setEditingProductId(product.id);
    setProductForm({
      name: product.name || "",
      description: product.description || "",
      price: product.price || "",
      image_url: product.image_url || "",
      image_file: null,
      category: product.category || "",
      available: product.available !== false,
    });
  };

  return (
    <div className="app-screen overflow-x-hidden bg-premium-grad text-foreground">
      <MobileHeader title="Marchand" />
      <main className="mx-auto w-full max-w-6xl space-y-5 px-4 pb-24 pt-3 sm:px-6 lg:px-8">
        <section className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-primary">Espace partenaire</div>
            <h1 className="font-heading text-3xl font-black tracking-tighter">Commandes et produits</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Restaurants et supermarches peuvent confirmer les commandes, verifier les paiements et mettre leur catalogue a jour.
            </p>
          </div>
          <Badge tone="success">Compte marchand</Badge>
        </section>

        <div className="grid gap-3 md:grid-cols-3">
          <Card className="p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Commandes actives</div>
            <div className="font-heading text-3xl font-black">{stats.activeOrders}</div>
          </Card>
          <Card className="p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Ventes confirmees</div>
            <div className="font-heading text-3xl font-black">{formatCDF(stats.sales)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Net marchand</div>
            <div className="font-heading text-3xl font-black text-secondary">{formatCDF(stats.net)}</div>
          </Card>
        </div>

        <Card className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <Storefront size={22} weight="duotone" />
              </span>
              <div>
                <h2 className="font-heading text-xl font-black">Mes etablissements</h2>
                <p className="text-xs text-muted-foreground">L'admin rattache un partenaire a votre email marchand.</p>
              </div>
            </div>
            <select
              value={selectedMerchant?.id || ""}
              onChange={(e) => setSelectedMerchantId(e.target.value)}
              className="min-h-11 rounded-full border border-white/10 bg-background px-4 text-sm font-bold"
            >
              {merchants.map((merchant) => (
                <option key={merchant.id} value={merchant.id}>{merchant.name}</option>
              ))}
            </select>
          </div>
          {selectedMerchant ? (
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Type</div>
                <div className="font-bold">{selectedMerchant.type === "supermarket" ? "Supermarche" : "Restaurant"}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Paiement</div>
                <div className="font-mono text-xs font-bold">{selectedMerchant.mpesa_phone || selectedMerchant.airtel_phone || selectedMerchant.orange_phone || "A configurer"}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Commission Nexora</div>
                <div className="font-bold">{Math.round(Number(selectedMerchant.commission_rate || 0) * 100)}%</div>
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-warning/25 bg-warning/10 p-4 text-sm text-warning">
              Aucun partenaire n'est encore rattache a ce compte. L'admin doit renseigner l'email du marchand dans le profil restaurant/supermarche.
            </div>
          )}
        </Card>

        <div className="grid gap-5 xl:grid-cols-[1.25fr_0.75fr]">
          <Card className="overflow-hidden">
            <div className="border-b border-white/10 p-4">
              <h2 className="font-heading text-xl font-black">Commandes recues</h2>
              <Input className="mt-3" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note optionnelle pour confirmation/refus" />
            </div>
            <div className="divide-y divide-white/10">
              {merchantOrders.map((order) => (
                <div key={order.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest text-primary">#{String(order.id).slice(0, 8).toUpperCase()}</div>
                      <h3 className="font-heading text-lg font-black">{order.customer_name || order.recipient_name}</h3>
                      <p className="text-xs text-muted-foreground">{order.recipient_phone} - {order.city_name || "Ville non definie"}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <StatusBadge status={order.status} />
                      <Badge tone={paymentTone(order.payment_status)}>{order.payment_status}</Badge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {(order.items || []).map((item) => (
                      <div key={`${order.id}-${item.product_id}`} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                        <div className="font-bold">{item.name}</div>
                        <div className="text-xs text-muted-foreground">x{item.quantity} - {formatCDF(item.line_total)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 grid gap-2 rounded-2xl bg-white/5 p-3 text-sm sm:grid-cols-3">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Total client</div>
                      <div className="font-mono font-black">{formatCDF(order.total_amount || order.items_subtotal)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Net marchand</div>
                      <div className="font-mono font-black text-secondary">{formatCDF(order.merchant_net)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Preuve</div>
                      {order.payment_proof_url ? (
                        <a className="font-bold text-primary underline" href={order.payment_proof_url} target="_blank" rel="noreferrer">Voir capture</a>
                      ) : (
                        <span className="text-muted-foreground">Aucune</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      disabled={busy.startsWith(order.id)}
                      onClick={() => orderAction(order.id, "payment/confirm", "Paiement confirme")}
                    >
                      <CheckCircle size={16} weight="bold" /> Paiement recu
                    </Button>
                    <Button
                      variant="outline"
                      disabled={busy.startsWith(order.id)}
                      onClick={() => orderAction(order.id, "payment/reject", "Paiement refuse")}
                    >
                      <XCircle size={16} weight="bold" /> Refuser preuve
                    </Button>
                    <Button
                      disabled={busy.startsWith(order.id)}
                      onClick={() => orderAction(order.id, "confirm", "Commande confirmee, livraison creee")}
                    >
                      <Package size={16} weight="bold" /> Preparer / livrer
                    </Button>
                    <Button
                      variant="danger"
                      disabled={busy.startsWith(order.id)}
                      onClick={() => orderAction(order.id, "cancel", "Commande annulee")}
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ))}
              {merchantOrders.length === 0 && (
                <div className="p-8 text-center text-sm text-muted-foreground">Aucune commande pour ce partenaire.</div>
              )}
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary/15 text-secondary">
                <ForkKnife size={22} weight="duotone" />
              </span>
              <div>
                <h2 className="font-heading text-xl font-black">Catalogue</h2>
                <p className="text-xs text-muted-foreground">Ajoutez ou modifiez les produits visibles par les clients.</p>
              </div>
            </div>

            <div className="mt-4 grid gap-3">
              <div>
                <Label>Nom</Label>
                <Input value={productForm.name} onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))} placeholder="Poulet grille, riz, pack eau..." />
              </div>
              <div>
                <Label>Categorie</Label>
                <Input value={productForm.category} onChange={(e) => setProductForm((f) => ({ ...f, category: e.target.value }))} placeholder="Repas, boissons, epicerie" />
              </div>
              <div>
                <Label>Prix CDF</Label>
                <Input type="number" value={productForm.price} onChange={(e) => setProductForm((f) => ({ ...f, price: e.target.value }))} placeholder="12000" />
              </div>
              <div>
                <Label>Image produit</Label>
                <div className="grid gap-2">
                  <Input value={productForm.image_url} onChange={(e) => setProductForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="Lien image optionnel" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => setProductForm((f) => ({ ...f, image_file: e.target.files?.[0] || null }))}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs"
                  />
                  {(productForm.image_file || productForm.image_url) && (
                    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-2">
                      <div className="h-16 w-16 overflow-hidden rounded-xl bg-black/30">
                        <img
                          src={productForm.image_file ? URL.createObjectURL(productForm.image_file) : productForm.image_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {productForm.image_file ? productForm.image_file.name : "Image externe"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={productForm.description} onChange={(e) => setProductForm((f) => ({ ...f, description: e.target.value }))} placeholder="Details utiles au client" />
              </div>
              <label className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-3 text-sm font-bold">
                Produit disponible
                <input type="checkbox" checked={productForm.available} onChange={(e) => setProductForm((f) => ({ ...f, available: e.target.checked }))} />
              </label>
              <Button onClick={submitProduct} disabled={busy === "product"}>
                {editingProductId ? "Mettre a jour" : "Ajouter produit"}
              </Button>
              {editingProductId && (
                <Button variant="outline" onClick={() => { setEditingProductId(""); setProductForm(emptyProduct); }}>
                  Annuler edition
                </Button>
              )}
            </div>

            <div className="mt-5 grid gap-3">
              <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                <Input
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Rechercher dans le catalogue..."
                />
                <select
                  value={productCategoryFilter}
                  onChange={(e) => setProductCategoryFilter(e.target.value)}
                  className="min-h-11 rounded-xl border border-white/10 bg-[#15171b] px-3 py-2 text-xs font-bold text-white [color-scheme:dark]"
                >
                  <option value="all">Toutes categories</option>
                  {productCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </div>
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => editProduct(product)}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 p-3 text-left press-down"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-black/30">
                      {product.image_url ? (
                        <img src={product.image_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-primary"><ForkKnife size={20} weight="duotone" /></span>
                      )}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-bold">{product.name}</span>
                      <span className="text-xs text-muted-foreground">{formatCDF(product.price)} - {product.available === false ? "masque" : "visible"}</span>
                    </span>
                  </span>
                  <PencilSimple size={18} weight="bold" className="shrink-0 text-primary" />
                </button>
              ))}
              {products.length === 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center text-sm text-muted-foreground">
                  Aucun produit dans ce catalogue.
                </div>
              )}
              {products.length > 0 && filteredProducts.length === 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center text-sm text-muted-foreground">
                  Aucun produit ne correspond aux filtres.
                </div>
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
